import { Injector, Type } from '@angular/core';
import { Node } from 'prosemirror-model';

import { mergeAttributes, Node as TipTapNode } from '@tiptap/core';
import { AngularNodeViewComponent, AngularNodeViewRenderer } from 'ngx-tiptap';
import { APP_TIPTAP_EDITOR_RENDER_AS } from '../tokens';

export type EmailVariableOptions = {
    HTMLAttributes: Record<string, any>;
};
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        emailVariable: {
            insertEmailVariable: (options: { variable?: string; format?: string; options?: string }) => ReturnType;

            updateEmailVariable: (options: { variable?: string; format?: string; options?: string }) => ReturnType;
        };
    }
}

export const createEmailVariableExtension = (injector: Injector, component: Type<AngularNodeViewComponent>) => {
    const renderAs = injector.get(APP_TIPTAP_EDITOR_RENDER_AS);

    function buildVariableText({ node, HTMLAttributes }: { node: Node; HTMLAttributes: Record<string, any> }) {
        const variable = node.attrs['variable'];
        const format = node.attrs['format'];
        const options = node.attrs['options'];

        if (!variable) {
            return '{{ \'\' }}';
        }

        if (format) {
            if (format === 'date') {
                return `{{ ${variable} | time_zone: "Mountain Standard Time" | format_date:"MMMM dd", "en-us" }}`;
            } else if (format === 'datetime') {
                return `{{ ${variable} | time_zone: "Mountain Standard Time" | format_date:"MMMM dd, yyyy h:mm tt", "en-us" }}`;
            }
        }

        return `{{ ${variable} }}`;
    }

    return TipTapNode.create<EmailVariableOptions>({
        name: 'emailVariable',
        inline: true,
        group: 'inline',
        selectable: true,
        draggable: true,
        atom: true,

        addOptions() {
            return {
                HTMLAttributes: {},
            };
        },

        addAttributes() {
            return {
                variable: {
                    default: '',
                },
                format: undefined,
                options: undefined,
            };
        },

        parseHTML() {
            return [{ tag: `[data-type="${this.name}"]` }];
        },

        renderHTML({ node, HTMLAttributes }) {
            return [
                'span',
                renderAs === 'html'
                    ? mergeAttributes(this.options.HTMLAttributes, {
                          // ...HTMLAttributes,
                          variable: HTMLAttributes['variable'],
                          format: HTMLAttributes['format'] || undefined,
                          options: HTMLAttributes['options'] || undefined,

                          style: HTMLAttributes['style'],
                          'data-type': this.name,
                      })
                    : mergeAttributes(this.options.HTMLAttributes, {
                        //   variable: HTMLAttributes['variable'],
                          style: HTMLAttributes['style'],
                        //   'data-type': this.name,
                      }),
                buildVariableText({ node, HTMLAttributes }),
            ];
        },

        addNodeView() {
            return AngularNodeViewRenderer(component, { injector });
        },

        addCommands() {
            return {
                insertEmailVariable:
                    options =>
                    ({ tr, dispatch, editor }) => {
                        const { selection } = tr;
                        const node = this.type.create(options);

                        if (dispatch) {
                            tr.replaceRangeWith(selection.from, selection.to, node);
                        }

                        return true;
                    },

                updateEmailVariable:
                    options =>
                    ({ chain, tr }) => {
                        const config: Record<string, string> = {};
                        const node = (tr as any).curSelection?.node;

                        if (!node || node.type.name !== this.name) {
                            return false;
                        }

                        if (options.variable !== undefined) {
                            config['variable'] = options.variable;
                        }

                        if (options.options !== undefined) {
                            config['options'] = options.options;
                        }

                        if (options.format !== undefined) {
                            config['format'] = options.format;
                        }

                        return chain().updateAttributes(this.name, config).run();
                    },
            };
        },
    });
};
